/** The spellbook: an index on the left page and the reading page on the right.
 *
 * The previous pass made both pages a list, and a list of twenty rows across a
 * 1560px spread leaves half of every row as blank parchment with the mana
 * figure stranded at the far edge. A book is an index *and* a page you read; the
 * left page is now a banded two-column index and the right page is the spell
 * itself - its illumination, what it does, what it costs and whether it can be
 * cast right now. Nothing on the spread instructs the player to click something
 * to find out what a spell does, because the spread already says.
 *
 * The index is **column-balanced**, not band-per-band. Laying each level band
 * out as its own two-column grid means a band with an odd count leaves a hole,
 * and the last band leaves a quarter of the page blank with an illuminated rule
 * underlining one card. The page is now measured and split at the point where
 * the two columns come out level, and a band that is cut by the split carries a
 * continuation rule at the head of the second column - which is what a printed
 * index does.
 *
 * Mana is shown at the hero's own mastery - `SpellDef.cost` is indexed by it, so
 * Expert Air Magic makes Forked Lightning cheaper by reading a different column
 * rather than by applying a discount - and a spell that cannot be cast right now
 * says so in the book's own voice. A resting screen never prints an error: the
 * kernel's refusal is a *state* until the player asks for the cast, and red type
 * on an untouched page reads as a fault in the game.
 */

import { useMemo, useState } from "react";
import type { ContentRegistry, HeroState, Reason, SpellDef, SpellDefId, SpellSchool } from "@game/core";
import { BitmapText, measureBitmapText, useDevicePixelRatio } from "../theme/bitmapText.js";
import { attachRefs, usePhoneSheetFit } from "../theme/phone.js";
import { spellIcon } from "./art.js";
import { MASTERY_NAME, groupDigits, titleCase } from "./format.js";
import { usePolishStyles } from "./polish.js";
import { MaybeIcon, ScreenStyles, useMeasure } from "./primitives.js";
import {
  SCHOOL_LABEL, castIllegality, filterSpells, masteryFor, spellManaCost, wisdomLevel,
} from "./spellModel.js";
import type { SpellBookTab } from "./spellModel.js";
import { explainReason } from "./townModel.js";
import { StyleLayer, useStyleSheet } from "../theme/styleSheet.js";

/* Twelve to a page, three across and four down, which is the reference's own
   leaf: the classic prints twelve spells a page as a picture with its name, its
   level and its cost under it, and the eighteen-row two-column index this
   replaces was the one thing on the spread a blind read named as a web list.
   The same count at both viewports, because a book you cannot give directions
   to is not an index. */
const PER_PAGE = 12;
const PER_PAGE_COMPACT = 12;
/* The illumination on the reading slip: spell art is 128px at source and is
   drawn at 1:1, the only scale the classic ever draws a spell picture at. The
   2x and 3x blow-ups this replaces filled a reading LEAF, and the leaf is gone:
   the recto is the next twelve spells now, the reference's own spread, and the
   reading is a slip under the book where a 1:1 miniature is the right size. */
export const SPELL_ART_PX = 128;
export const READ_ICON = 128;
export const READ_ICON_COMPACT = 128;
/* A cell's picture on the leaf: the 128px source at 1:1 on the roomy leaf and
   at a half on the compact one. The reference draws its spell pictures about
   68 wide on a 366px leaf - a sixth of the page - and a 760px leaf at 1080p
   puts the whole frame at the same share. Both are whole ratios. */
const SPELL_CELL_ART = 128;
/** Widest name the display face may set in a spread cell, in glyph px at
 *  magnification 1: the 1080p cell is ~230px and 4px of padding is inside it. */
const CELL_NAME_MAX_PX = 216;

/**
 * The widest caption a cell holds on one line, in glyph px of the serif
 * CAPTION face - the face every serif reading is held to on a 1080-tall
 * window (`deviceCapFloor`). A 1080p cell is about 245 CSS px; at 80% the
 * same cell holds 245 / 1.25 = 196 glyph px, and that is the budget, so a
 * caption that fits at 80% fits at 100% too.
 */
const CELL_NAME_LINE_PX = 196;

/**
 * The caption the book prints for `name`: the name itself when it fits one
 * line, else the reference's own abbreviation - The classic's book prints
 * `Gale Spirit` for Summon Gale Spirit and `Prot. from Water` for
 * Tide Ward (measured off the expansion spellbook reference) - and
 * only when even that is too wide, two lines broken at the space nearest the
 * middle. Widths are measured in the caption face, the widest the cell is
 * ever set in.
 */
function nameLines(name: string): readonly string[] {
  const fits = (s: string): boolean => measureBitmapText(s, { face: "serifCaption" }).width <= CELL_NAME_LINE_PX;
  if (fits(name)) return [name];
  const short = name.replace(/^Summon /, "").replace(/^Protection from /, "Prot. from ");
  if (fits(short)) return [short];
  const mid = short.length / 2;
  let at = -1;
  for (let i = short.indexOf(" "); i !== -1; i = short.indexOf(" ", i + 1)) {
    if (at === -1 || Math.abs(i - mid) < Math.abs(at - mid)) at = i;
  }
  return at === -1 ? [short] : [short.slice(0, at), short.slice(at + 1)];
}

/** "1st", "2nd", "3rd", "4th", "5th": the reference's own level caption. */
const ordinal = (n: number): string =>
  `${String(n)}${n === 1 ? "st" : n === 2 ? "nd" : n === 3 ? "rd" : "th"}`;
const SPELL_CELL_ART_COMPACT = 64;

/**
 * The device on a ribbon bookmark. The classic's school ribbons are told
 * apart by a painted sigil - a gust for air, a wave for water, a flame for
 * fire, a sprig for earth - and only secondarily by colour; a coloured
 * rectangle with a diamond on it is a filter chip. Gold line-work, two
 * passes: a dark under-stroke for the relief and the gilt over it.
 */
const SIGIL_PATH: Readonly<Record<string, string>> = Object.freeze({
  air: "M3 8 C7 4 11 4 14 8 S20 12 23 8 M2 13 C6 9 10 9 13 13 S19 17 22 13 M5 18 C9 14 13 14 16 18 S21 21 23 18",
  water: "M2 9 Q6 4 10 9 T18 9 T24 9 M2 15 Q6 10 10 15 T18 15 T24 15 M2 21 Q6 16 10 21 T18 21 T24 21",
  fire: "M13 2 C13 8 7 9 7 15 C7 19 10 22 13 22 C16 22 19 19 19 15 C19 12 17 10 16 8 C16 11 14 12 13 12 C13 9 14 6 13 2 Z M13 14 C12 16 11 17 11 18.5 C11 20 12 21 13 21 C14 21 15 20 15 18.5 C15 17 14 16 13 14 Z",
  earth: "M13 23 L13 6 M13 12 C10 12 8 10 7 7 C10 7 12 9 13 12 M13 16 C16 16 18 14 19 11 C16 11 14 13 13 16 M13 8 C11 8 10 6 9 4 C11 4 12 6 13 8 M13 20 C10 20 8 18 7 15 C10 15 12 17 13 20",
  all: "M13 2 L15.5 9.5 L23 9.5 L17 14 L19.5 21.5 L13 17 L6.5 21.5 L9 14 L3 9.5 L10.5 9.5 Z",
  combat: "M4 4 L21 21 M21 4 L4 21 M4 4 L8 4 L4 8 Z M21 4 L17 4 L21 8 Z M18 18 L21 21 L21 18 M8 18 L4 21 L4 18",
  adventure: "M13 2 L16 10 L24 13 L16 16 L13 24 L10 16 L2 13 L10 10 Z M13 8 L13 18 M8 13 L18 13",
});

function Sigil(props: { readonly kind: string }): JSX.Element {
  const d = SIGIL_PATH[props.kind] ?? SIGIL_PATH["all"]!;
  const filled = props.kind === "fire" || props.kind === "all";
  return (
    <svg className="hm-sb__sigil" viewBox="0 0 26 26" aria-hidden="true">
      <path d={d} fill={filled ? "rgba(0,0,0,0.55)" : "none"} stroke="rgba(0,0,0,0.55)" strokeWidth="3"
        strokeLinecap="round" strokeLinejoin="round" transform="translate(0.8,1.2)" />
      <path d={d} fill={filled ? "url(#hm-sb-gilt)" : "none"} stroke="url(#hm-sb-gilt)" strokeWidth="1.9"
        strokeLinecap="round" strokeLinejoin="round" />
      <defs>
        <linearGradient id="hm-sb-gilt" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#fff2c2" />
          <stop offset="0.5" stopColor="#e6bd58" />
          <stop offset="1" stopColor="#a5741c" />
        </linearGradient>
      </defs>
    </svg>
  );
}

/* What one entry and one rule cost the column, for the balance. They are the
   laid-out heights, so they are stated once here and once in the sheet below;
   being a few pixels out only shifts the split by one entry. */
const ROW_H = 76;
const RULE_H = 32;

/** The four school masteries, in the order `SpellDef.cost` is indexed. */
const MASTERY_LADDER: readonly string[] = Object.freeze(["None", "Basic", "Advanced", "Expert"]);

/**
 * The line under the title: how much mana, and how far the hero's Wisdom reaches.
 *
 * Two sentences, not two fields joined by a middot: the classic writes prose in
 * its books and joins nothing with a separator character, and the middot was
 * the thing a reviewer named when they picked our spellbook out of a blind
 * pair.
 *
 * It used to read "Mana 62 - casts to level 1", which has no subject at all -
 * nothing in the clause says what casts, so the only available reading is that
 * the *mana* casts to level 1, which is not a rule the game has. Wisdom is what
 * caps the level (`WISDOM_SPELL_LEVEL`), so Wisdom is the noun. Pure and
 * exported so a test can hold the sentence to having one.
 */
export function bookSubtitle(mana: number, wisdom: number): string {
  return `${groupDigits(mana)} mana. Wisdom carries spells to level ${String(wisdom)}.`;
}

type IndexItem =
  | { readonly kind: "rule"; readonly level: number; readonly cont: boolean }
  | { readonly kind: "spell"; readonly spell: SpellDef };

export interface SpellBookProps {
  readonly content: ContentRegistry;
  readonly hero: HeroState;
  /** Which page opens first, and which legality test applies. */
  readonly initialTab?: SpellBookTab;
  readonly inBattle?: boolean;
  readonly castThisRound?: boolean;
  /** From the kernel, when the host can afford a probe. Falls back to the mirror. */
  readonly castReason?: ((spell: SpellDefId) => Reason | null) | undefined;
  readonly manaCost?: ((spell: SpellDefId) => number) | undefined;
  readonly onCast: (spell: SpellDefId) => void;
  readonly onClose: () => void;
}

export function SpellBook(props: SpellBookProps): JSX.Element {
  usePolishStyles();
  useBookStyles();
  const { content, hero } = props;
  const [rootRef, box] = useMeasure<HTMLDivElement>();
  const fit = usePhoneSheetFit();
  // The DEVICE height decides the set: a 768-tall window at 80% zoom
  // measures 960 CSS px and has the same 768 rows of glass it had at 100%,
  // and the roomy set's four rows of captions, held to the cap floor, ran
  // into the icons under them there.
  const dpr = useDevicePixelRatio();
  const dh = box.h * Math.min(1, dpr);
  const dw = box.w * Math.min(1, dpr);
  const compact = box.h > 0 && box.w > 0 && (dh < 880 || dw < 1500);
  const [tab, setTab] = useState<SpellBookTab>(props.initialTab ?? "combat");
  const [school, setSchool] = useState<SpellSchool | null>(null);
  const [page, setPage] = useState(0);
  const [chosen, setChosen] = useState<SpellDefId | null>(null);

  const schools = useMemo(() => schoolsOf(content, hero, tab), [content, hero, tab]);
  const spells = useMemo(
    () => filterSpells(content, hero, { tab, school }),
    [content, hero, tab, school],
  );

  /* A SPREAD is two leaves of twelve, which is what the classic opens to: the
     verso and the recto are both index, and the page turn moves both. */
  const per = compact ? PER_PAGE_COMPACT : PER_PAGE;
  const pages = Math.max(1, Math.ceil(spells.length / (2 * per)));
  const shown = page >= pages ? pages - 1 : page;
  const sheet = useMemo(
    () => spells.slice(shown * 2 * per, shown * 2 * per + per),
    [spells, shown, per],
  );
  const recto = useMemo(
    () => spells.slice(shown * 2 * per + per, shown * 2 * per + 2 * per),
    [spells, shown, per],
  );

  const ctx = { inBattle: props.inBattle === true, castThisRound: props.castThisRound === true };
  const reasonFor = (id: SpellDefId): Reason | null =>
    props.castReason?.(id) ?? castIllegality(content, hero, id, ctx);
  const costFor = (id: SpellDefId): number =>
    props.manaCost?.(id) ?? spellManaCost(content, hero, id);

  /* The reading page is never blank. With nothing picked it opens on the first
     spell of the index, which is what an open book looks like. */
  const detail = useMemo((): SpellDef | null => {
    const picked = chosen === null ? undefined : content.spells.get(chosen);
    return picked ?? sheet[0] ?? null;
  }, [chosen, content, sheet]);

  const pick = (tabNext: SpellBookTab): void => {
    setTab(tabNext); setSchool(null); setPage(0); setChosen(null);
  };

  /* One spell on the leaf, the reference's way: the picture, and under it the
     name, the level with the hero's mastery, and the cost. The whole cell is
     the button; the reading page beside it says the rest. */
  const renderCell = (s: SpellDef): JSX.Element => {
    const reason = reasonFor(s.id);
    const cost = costFor(s.id);
    const short = hero.mana < cost;
    const mastery = MASTERY_NAME[masteryFor(hero, s.school)] ?? "None";
    const cls = [
      "hm-sb__cell",
      reason === null ? "" : "hm-sb__cell--barred",
      detail?.id === s.id ? "hm-sb__cell--on" : "",
    ].filter((c) => c.length > 0).join(" ");
    return (
      <button
        key={s.id as string}
        type="button"
        className={cls}
        /* Never `disabled`. A refused spell is still readable, still
           selectable, and still shows its art at full colour; the refusal
           belongs on the Cast plate and on the reading page. */
        aria-disabled={reason !== null}
        title={s.name}
        onClick={() => setChosen(s.id)}
        onDoubleClick={() => { if (reason === null) props.onCast(s.id); }}
      >
        <span className="hm-sb__cellart">
          <MaybeIcon atlasKey={spellIcon(s)} size={compact ? SPELL_CELL_ART_COMPACT : SPELL_CELL_ART}
            title={s.name} />
        </span>
        {/* Stamped in the serif faces, like every caption in the game: the
            name in the display face, the level and the cost in the text face.
            No shadow - this is dark ink on parchment, and the classic's book sets
            its captions plain. */}
        {/* The text face on the compact leaf: "Summon Stone Spirit" in the
            display face is 200px, which is the whole of a 1366 cell, and a
            centred nowrap caption loses a letter off BOTH ends. */}
        {/* Every caption is held to the window's cap floor (15 rows on a
            1080p window), and "Summon Stone Spirit" at that size is wider
            than any cell at either viewport; a centred nowrap caption lost
            letters off BOTH ends. `nameLines` prints the reference's own
            abbreviation for those, and wraps only as a last resort. Three
            lines under the picture - name, level with mastery, cost - is what
            the reference's book prints. */}
        {nameLines(s.name).map((line, i) => (
          <span className="hm-sb__cellname" key={i}>
            <BitmapText
              face={compact || measureBitmapText(s.name, { face: "serifDisplay" }).width > CELL_NAME_MAX_PX ? "serif" : "serifDisplay"}
              scale={1}
            >
              {line}
            </BitmapText>
          </span>
        ))}
        <span className="hm-sb__cellmeta">
          <BitmapText face="serif" scale={1}>
            {`${ordinal(s.level)} lev${mastery === "None" ? "" : `/${mastery.slice(0, 3)}`}`}
          </BitmapText>
        </span>
        <span className={`hm-sb__cellcost ${short ? "hm-sb__cellcost--short" : ""}`}>
          <BitmapText face="serif" scale={1}>{`Spell Points: ${String(cost)}`}</BitmapText>
        </span>
      </button>
    );
  };

  return (
    <div
      className={`hs-screen ${compact ? "hs-book--compact" : ""} ${fit.screenClass}`.trim()}
      ref={(el) => { attachRefs(el, rootRef, fit.screenRef); }}
      style={fit.style}
      role="region"
      aria-label="Spellbook"
    >
      <ScreenStyles />

      <div className="hs-phone-sheet" ref={fit.sheetRef}>
      <div className="hs-screen__bar hs-plank">
        <span className="hs-screen__title">Spellbook</span>
        <span className="hs-screen__sub">{bookSubtitle(hero.mana, wisdomLevel(hero))}</span>
        <span className="hs-spacer" />
        <button type="button" className="hs-plate hs-plate--sm" onClick={props.onClose}>Close</button>
      </div>

      <div className="hs-book hm-sb__book">
        <div className="hs-book__spread">
          {/* The two ribbon stacks hang off the OUTER EDGES of the book, which
              is where the classic hangs them: two on the verso cover for what
              kind of book this is, and the school ribbons on the recto. They
              are absolutely positioned against the spread, so they sit on the
              leather rather than inside a page - a tab strip laid out INSIDE
              the leaf is a filter bar, whatever it is shaped like. */}
          <div className="hm-sb__ribbons hm-sb__ribbons--left" role="group" aria-label="Book">
            <button type="button" aria-label="Combat"
              className={`hm-sb__ribbon hm-sb__ribbon--combat ${tab === "combat" ? "hm-sb__ribbon--on" : ""}`}
              title="Combat spells"
              onClick={() => pick("combat")}>
              <Sigil kind="combat" />
            </button>
            <button type="button" aria-label="Adventure"
              className={`hm-sb__ribbon hm-sb__ribbon--adv ${tab === "adventure" ? "hm-sb__ribbon--on" : ""}`}
              title="Adventure spells"
              onClick={() => pick("adventure")}>
              <Sigil kind="adventure" />
            </button>
          </div>
          <div className="hm-sb__ribbons hm-sb__ribbons--right" role="group" aria-label="Magic school">
            <button type="button" aria-label="All schools"
              className={`hm-sb__ribbon hm-sb__ribbon--all ${school === null ? "hm-sb__ribbon--on" : ""}`}
              title="Every school"
              onClick={() => { setSchool(null); setPage(0); }}>
              <Sigil kind="all" />
            </button>
            {schools.map((sc) => (
              <button key={sc} type="button" aria-label={SCHOOL_LABEL[sc]}
                className={`hm-sb__ribbon hm-sb__ribbon--${sc} ${school === sc ? "hm-sb__ribbon--on" : ""}`}
                title={`${SCHOOL_LABEL[sc]} — ${MASTERY_NAME[masteryFor(hero, sc)] ?? "None"} mastery`}
                onClick={() => { setSchool(sc); setPage(0); }}>
                <Sigil kind={sc} />
              </button>
            ))}
          </div>

          {/* ------------------------------------------------- the verso */}
          <div className="hs-book__page hs-book__page--left">
            {/* No running head: the classic's leaves start with the pictures,
                and the bar over the book already says what book this is.
                The head's 45px is what the fourth row of pictures needs once
                the slip is under the spread. */}
            <div className="hs-book__list hm-sb__grid" aria-label={school === null ? `${titleCase(tab)} spells` : `${SCHOOL_LABEL[school]} magic`}>
              {spells.length === 0 ? (
                <div className="hs-tiny hm-sb__blank">
                  Nothing is written on this page.
                </div>
              ) : sheet.map(renderCell)}
            </div>
            <div className="hs-book__foot">
              {/* The page-turn corner. A bound book is turned by its corner, not
                  by a pair of labelled plates in the middle of the leaf, and the
                  two corners are the outer ones - back on the verso, forward on
                  the recto. The label survives for a screen reader. */}
              <button type="button"
                className="hm-sb__turn hm-sb__turn--back"
                disabled={shown <= 0}
                aria-label="Previous page"
                title={shown <= 0 ? "This is the first page" : "Previous page"}
                onClick={() => setPage(shown - 1)} />
              <span className="hs-spacer" />
              {/* Stamped in the same serif as the cell captions. Typeset,
                  letterspaced small caps with a text shadow was the one soft
                  line on the leaf: measured 72-85 grey levels and half of its
                  ink at mid-tone beside captions at 1-4 levels. */}
              <span className="hs-book__folio hm-sb__folio">
                <BitmapText face="serif" scale={1}>
                  {school === null
                    ? `${String(spells.length)} spells`
                    : `${String(spells.length)} ${SCHOOL_LABEL[school]} spells`}
                </BitmapText>
              </span>
            </div>
          </div>

          <div className="hs-book__gutter" aria-hidden="true" />

          {/* ------------------------------------------------- the recto.
              The next twelve, under the same running head: the classic's book
              is two leaves of index, and the reading page this replaces was
              the one thing on the spread the reference does not have. A recto
              with nothing left to print stays blank parchment, as a book's
              last leaf does. */}
          <div className="hs-book__page hs-book__page--right">
            <div className="hs-book__list hm-sb__grid">
              {recto.map(renderCell)}
            </div>
            <div className="hs-book__foot">
              <span className="hs-book__folio hm-sb__folio hm-sb__folio--lead">
                <BitmapText face="serif" scale={1}>
                  {`Page ${String(shown + 1)} of ${String(Math.max(1, pages))}`}
                </BitmapText>
              </span>
              <span className="hs-spacer" />
              <button type="button"
                className="hm-sb__turn hm-sb__turn--fwd"
                disabled={shown >= pages - 1}
                aria-label="Next page"
                title={shown >= pages - 1 ? "This is the last page" : "Next page"}
                onClick={() => setPage(shown + 1)} />
            </div>
          </div>
        </div>

        {/* ------------------------------------------------- the slip.
            What the chosen spell is, does and costs, and the plate that casts
            it, on a slip of parchment tucked under the book where the reference
            hangs its pennants. Never blank: with nothing picked it reads the
            first spell of the spread. */}
        <div className="hm-sb__slip" role="region" aria-label="Chosen spell">
          {detail === null ? (
            <div className="hs-tiny hm-sb__slip__empty">
              This hero carries no spells of this kind.
            </div>
          ) : (
            <SpellReading
              content={content}
              hero={hero}
              spell={detail}
              iconSize={compact ? READ_ICON_COMPACT : READ_ICON}
              cost={costFor(detail.id)}
              reason={reasonFor(detail.id)}
              inBattle={props.inBattle === true}
              onCast={props.onCast}
            />
          )}
        </div>
      </div>

      {detail === null ? null : (
        <div className="hs-sr" aria-live="polite">
          {explainReasonOrEmpty(content, reasonFor(detail.id))}
        </div>
      )}
      </div>
    </div>
  );
}

const explainReasonOrEmpty = (content: ContentRegistry, r: Reason | null): string =>
  r === null ? "" : explainReason(content, r);

/**
 * Deal one page's spells into two columns of equal height.
 *
 * Items are laid out in reading order and cut at the point where the two halves
 * are closest to level; the cut is never made straight after a level rule,
 * because a rule at the foot of a column underlines nothing. A band that the cut
 * falls inside carries a continuation rule at the head of the second column.
 */
export function splitIndex(
  sheet: readonly SpellDef[],
): { readonly left: readonly IndexItem[]; readonly right: readonly IndexItem[] } {
  const flat: IndexItem[] = [];
  let level = -1;
  for (const s of sheet) {
    if (s.level !== level) { flat.push({ kind: "rule", level: s.level, cont: false }); level = s.level; }
    flat.push({ kind: "spell", spell: s });
  }
  const cost = (it: IndexItem): number => (it.kind === "rule" ? RULE_H : ROW_H);
  const total = flat.reduce((n, it) => n + cost(it), 0);

  let cut = flat.length;
  let bestGap = Number.POSITIVE_INFINITY;
  let acc = 0;
  for (let i = 0; i <= flat.length; i += 1) {
    const prev = flat[i - 1];
    const legal = i === 0 || i === flat.length || prev === undefined || prev.kind !== "rule";
    const gap = Math.abs(total - acc - acc);
    /* `<=` so a tie takes the *later* cut: with one entry on the page the two
       extremes tie, and the entry belongs at the head of the left column rather
       than alone on the right. It also makes an even page left-heavy, which is
       the way a printed index breaks. */
    if (legal && gap <= bestGap) { bestGap = gap; cut = i; }
    const cur = flat[i];
    if (cur !== undefined) acc += cost(cur);
  }

  const left = flat.slice(0, cut);
  const right = flat.slice(cut);
  const head = right[0];
  if (head !== undefined && head.kind === "spell") {
    right.unshift({ kind: "rule", level: head.spell.level, cont: true });
  }
  return { left, right };
}

/** The schools present in this hero's book under one tab, for the tab strip. */
function schoolsOf(
  content: ContentRegistry, hero: HeroState, tab: SpellBookTab,
): readonly SpellSchool[] {
  const seen: SpellSchool[] = [];
  for (const s of filterSpells(content, hero, { tab, school: null })) {
    if (!seen.includes(s.school)) seen.push(s.school);
  }
  return seen;
}

// ----------------------------------------------------------- reading page

function SpellReading(props: {
  readonly content: ContentRegistry;
  readonly hero: HeroState;
  readonly spell: SpellDef;
  readonly iconSize: number;
  readonly cost: number;
  readonly reason: Reason | null;
  readonly inBattle: boolean;
  readonly onCast: (id: SpellDefId) => void;
}): JSX.Element {
  const { spell, hero } = props;
  const mastery = masteryFor(hero, spell.school);
  const castableIn = spell.combat
    ? spell.adventure ? "Battle and the map" : "Battle only"
    : "The map only";

  /* The ladder is only worth its 750x50 band when the four columns differ. Four
     identical numbers tell the player nothing and are four more flat cells on a
     page that is otherwise all material. */
  const rungs = MASTERY_LADDER.map((name, i) => ({ name, cost: spell.cost[i] ?? props.cost }));
  const varies = rungs.some((r) => r.cost !== rungs[0]?.cost);

  /* Why the plate is dark, in the book's voice.
     `castIllegality` answers two quite different questions with one
     `TARGET_INVALID` - "this is not the page for that spell" and "this hero's
     Wisdom does not reach that level" - and "Invalid target." is neither of
     them in a player's language. Both are decided by data the screen already
     holds (the spell's own `combat`/`adventure` flags, its level, and the
     hero's wisdom), so naming them is reading the same state the kernel read,
     not paraphrasing a rule. Anything else keeps the kernel's own words. And
     none of it is an error: at rest the player has not asked for a cast, so the
     line is ink on parchment rather than the validator's red. */
  const wisdom = wisdomLevel(hero);
  const restingNote = (): string => {
    if (!props.inBattle && spell.combat && !spell.adventure) {
      return "This spell can only be cast in battle.";
    }
    if (props.inBattle && spell.adventure && !spell.combat) {
      return "This spell can only be cast out on the map.";
    }
    if (spell.level > wisdom) {
      return `Wisdom carries this hero to level ${String(wisdom)} spells; this one is level ${String(spell.level)}.`;
    }
    return explainReason(props.content, props.reason as Reason);
  };
  const note = props.reason === null
    ? { text: "Ready to cast.", tone: "ready" as const }
    : { text: restingNote(), tone: "rest" as const };

  /* The cost at each mastery, as one line rather than a ladder of four
     plates: it is only worth printing when the four columns differ. */
  const ladder = rungs.map((r) => `${r.name} ${String(r.cost)}`).join(" · ");

  return (
    <>
      {/* The illumination, at 1:1 - the only scale the reference draws a spell
          picture at. The art is cut out by its own alpha, so the slip supplies
          the ground: a painted roundel on vellum with a gilt rule round it. */}
      <div className="hs-read__plate">
        <span className="hm-sb__illum">
          <MaybeIcon atlasKey={spellIcon(spell)} size={props.iconSize} title={spell.name} />
        </span>
      </div>
      <div className="hs-read hm-sb__read">
        <div className="hs-read__banner">
          <span className="hm-sb__slipname">
            <BitmapText face="serifDisplay" scale={2}>{spell.name}</BitmapText>
          </span>
          <span className={`hs-spell__school hs-spell__school--${spell.school}`} aria-hidden="true" />
          {/* Three facts, three marks, a word of air between them - not one
              string joined by middots, which is a dashboard's idiom and not
              this game's. And no mastery line at all when the hero has none:
              `None mastery` is an enum leaking through the copy. */}
          <span className="hs-read__kind hm-sb__kind">
            <BitmapText face="serif" scale={1}>{`Level ${String(spell.level)}`}</BitmapText>
            <BitmapText face="serif" scale={1}>{`${SCHOOL_LABEL[spell.school]} magic`}</BitmapText>
            {mastery <= 0 ? null : (
              <BitmapText face="serif" scale={1}>
                {`${MASTERY_NAME[mastery] ?? ""} mastery`}
              </BitmapText>
            )}
          </span>
          <span className="hs-spacer" />
          <span className="hs-spell__cost hs-spell__cost--big" title={`${String(props.cost)} mana`}>
            {props.cost}
          </span>
        </div>
        <p className="hs-read__body hm-sb__body">{spell.description}</p>
        <div className="hs-read__facts hm-sb__facts">
          {[
            ["Cast in", castableIn],
            ["Your mana", groupDigits(hero.mana)],
            varies ? ["Mana by mastery", ladder]
              : ["Mana", `${String(rungs[0]?.cost ?? props.cost)} at any mastery`],
          ].map(([label, value]) => (
            <div className="hm-sb__fact" key={label}>
              {/* Both share the face's descent band so `Cast in` and `Battle only`
                  centre on one baseline: without it the `y` made the value's box
                  three rows taller and `align-items: center` lifted the label. */}
              <BitmapText face="serif" scale={1} reserveDescent>{label ?? ""}</BitmapText>
              <span className="hm-sb__factrule" aria-hidden="true" />
              <BitmapText face="serif" scale={1} reserveDescent>{value ?? ""}</BitmapText>
            </div>
          ))}
        </div>
      </div>
      <div className="hs-read__act">
        <button
          type="button"
          className={`hm-sb__cast ${props.reason === null ? "" : "hm-sb__cast--off"}`}
          disabled={props.reason !== null}
          title={props.reason === null ? "Cast" : note.text}
          onClick={() => props.onCast(spell.id)}
        >
          <BitmapText face="serifDisplay" shadow={props.reason === null} scale={2}>CAST</BitmapText>
        </button>
        <span className={note.tone === "ready" ? "hs-read__ready" : "hm-sb__note"}>{note.text}</span>
      </div>
    </>
  );
}

// ------------------------------------------------------------------ styles

const BOOK_STYLE_ID = "hm-spellbook-art";

/* Appended after `polish.ts`, so an equal-specificity rule here wins on document
   order rather than on `!important`. */
const BOOK_CSS = `
/* ---- no matte, and no dodge. The spell frames carry real alpha, so the art is
   composited by the browser against whatever the book paints behind it.

   Two compensations lived here and are gone. First hm-relief__inlay, a screen
   blend, which got the ground right by accident and washed the art: 26.8/255
   mean per-channel error on the 64px Bless tile, max 57. Then an SVG matte that
   set alpha := r+g+b, which keyed on pure black - correct while the packer was
   flattening these frames to opaque black, and destructive once it stopped,
   because 3,566 texels across the 69 spell frames are opaque pure black that
   belongs to the artwork. spell.fire.battle_fury is 7.8% pure-black ink and
   would have lost all of it.

   If the art ever looks washed or holed again, measure the atlas frame before
   reaching for a filter. Both of these were renderer fixes for a pipeline bug. */

/* The illumination: a painted cartouche on the leaf, not a sprite in a socket.
   the classic lays its spell art straight onto the parchment and rules a painted
   frame round it. Ours sat on a bronze recess over a near-black ink field
   (#0d0904), which is a socket - a machined part with a miniature dropped
   into it - and a socket is exactly the "diagram, not object" reading this
   screen lost a blind comparison on.

   The art is 62% transparent (10,102 of 16,384 texels on spell.water.bless
   are alpha 0), so what is behind it is the page. It therefore has to BE the
   page: vellum, a painted roundel of size behind the miniature so the drawing's
   own darks have something to sit in, a gilt double rule and brackets at the
   four corners. Nothing here keys, mattes or blends the art - see the note at
   the head of this sheet. */
.hs-read__plate {
  position: relative;
  align-self: stretch;
  flex: 1 1 auto;
  min-height: 0;
  padding: 0;
  background: none;
  box-shadow: none;
}
.hm-sb__illum {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 16px;
  background-color: #e3cf9f;
  background-image:
    /* The painted disc the miniature stands on, with an EDGE. A soft radial
       with nothing to end on reads as a smudge on the page rather than as a
       ground somebody laid down: the two hairline rings are the whole
       difference between a wash and a painted roundel. */
    radial-gradient(closest-side circle at 50% 46%,
      rgba(0,0,0,0) 0 39.5%, rgba(150,112,52,0.34) 39.5% 40.6%,
      rgba(0,0,0,0) 40.6% 44%, rgba(150,112,52,0.2) 44% 44.8%,
      rgba(0,0,0,0) 44.8%),
    radial-gradient(46% 46% at 50% 46%,
      rgba(255,250,232,0.95) 0%, rgba(236,212,164,0.7) 54%,
      rgba(176,140,80,0.30) 78%, rgba(0,0,0,0) 88%),
    /* Foxing. Vellum ages in blooms, and they are what stops a leaf of it
       measuring as one value across a 32px block. */
    radial-gradient(circle at 21% 17%, rgba(146,108,50,0.18) 0 7px, rgba(0,0,0,0) 15px),
    radial-gradient(circle at 78% 63%, rgba(146,108,50,0.15) 0 10px, rgba(0,0,0,0) 21px),
    radial-gradient(circle at 55% 88%, rgba(146,108,50,0.13) 0 6px, rgba(0,0,0,0) 16px),
    /* Laid and chain lines. At 5% these measured as flat paper - a 32px block
       came out at a standard deviation of about 3.5, which is under the 4 an
       art director's blank-block count treats as empty. Real laid paper is
       plainly visible when you hold it to the light. */
    repeating-linear-gradient(91deg, rgba(112,84,42,0.10) 0 1px, rgba(0,0,0,0) 1px 4px),
    repeating-linear-gradient(1deg, rgba(112,84,42,0.055) 0 1px, rgba(0,0,0,0) 1px 6px),
    radial-gradient(120% 130% at 50% 8%, #f4e6c0 0%, #dcc79a 52%, #bca476 100%);
  background-size:
    auto, auto, 149px 131px, 191px 167px, 127px 113px, auto, auto, auto;
  box-shadow:
    inset 0 0 0 1px rgba(96,72,32,0.5),
    inset 0 0 0 4px rgba(244,232,200,0.55),
    inset 0 0 0 5px rgba(150,112,40,0.8),
    inset 0 0 0 7px rgba(244,232,200,0.35),
    inset 0 0 36px rgba(140,104,48,0.26);
}
/* The painter's corner brackets. Eight hairlines, two to a corner - which is
   what a hand-ruled cartouche is, and what a border is not. */
.hm-sb__illum::before {
  content: "";
  position: absolute;
  inset: 12px;
  pointer-events: none;
  background-image:
    linear-gradient(#8a6a1e, #8a6a1e), linear-gradient(#8a6a1e, #8a6a1e),
    linear-gradient(#8a6a1e, #8a6a1e), linear-gradient(#8a6a1e, #8a6a1e),
    linear-gradient(#8a6a1e, #8a6a1e), linear-gradient(#8a6a1e, #8a6a1e),
    linear-gradient(#8a6a1e, #8a6a1e), linear-gradient(#8a6a1e, #8a6a1e);
  background-repeat: no-repeat;
  background-size:
    38px 2px, 2px 38px, 38px 2px, 2px 38px,
    38px 2px, 2px 38px, 38px 2px, 2px 38px;
  background-position:
    left top, left top, right top, right top,
    left bottom, left bottom, right bottom, right bottom;
  opacity: 0.72;
}

/* ---- the page-turn corners. Drawn as the leaf itself, lifted: a triangle of
   paper a shade lighter than the page, a hard fold line along the diagonal, and
   the shadow of the lifted corner under it. The chevron is cut out of the paper
   rather than printed on a plate, because a plate is furniture and this is the
   page. */
.hm-sb__turn {
  position: relative;
  flex: 0 0 auto;
  width: 52px;
  height: 38px;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  transition: filter 90ms linear, transform 90ms linear;
}
.hm-sb__turn::before {
  content: "";
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(120deg, #fff6da 0%, #e8d2a2 44%, #c2a771 100%);
  box-shadow: inset 0 0 0 1px rgba(96,72,32,0.45);
}
.hm-sb__turn::after {
  content: "";
  position: absolute;
  bottom: 7px;
  width: 0; height: 0;
  border-top: 7px solid transparent;
  border-bottom: 7px solid transparent;
}
/* The right angle sits at the book's OUTER corner on each leaf, which is the
   corner a hand actually takes. */
.hm-sb__turn--back::before {
  clip-path: polygon(0% 0%, 0% 100%, 100% 100%);
  background-image: linear-gradient(45deg, #fff6da 0%, #e8d2a2 46%, #c2a771 100%);
}
.hm-sb__turn--back::after {
  left: 8px;
  border-right: 11px solid #5b4520;
}
.hm-sb__turn--fwd::before {
  clip-path: polygon(100% 0%, 100% 100%, 0% 100%);
  background-image: linear-gradient(-45deg, #fff6da 0%, #e8d2a2 46%, #c2a771 100%);
}
.hm-sb__turn--fwd::after {
  right: 8px;
  border-left: 11px solid #5b4520;
}
.hm-sb__turn:hover:not(:disabled) { filter: brightness(1.06); transform: translateY(-1px); }
.hm-sb__turn:disabled { cursor: default; opacity: 0.28; }
.hm-sb__folio--lead { margin-left: 0; }
/* A bitmap mark: the typeset rules on \`.hs-book__folio\` (size, tracking,
   case) do nothing to an svg; what it needs is a line box of its own height. */
/* And the cell captions' ink, not the folio's lighter one: stamped on the
   leaf's own texture the #4a3414 measured 4.45:1 at 1366x768, a twentieth
   under this lane's 4.5:1 floor. #3a2810 is what the three caption lines in
   every cell are set in and measures 5.6:1 there. */
.hm-sb__folio { display: flex; align-items: center; line-height: 0; color: #3a2810; }

/* ---- the leaf: twelve pictures, three across, each captioned the way the
   reference captions its own - name, level and mastery, cost. The cell is
   the whole button and the picture sits on a painted roundel of parchment
   with a gilt hairline round it, the same treatment the illumination on the
   facing page gets at three times the size. */
.hs-book__list.hm-sb__grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  grid-auto-rows: minmax(0, 1fr);
  align-items: stretch;
  /* 2px between rows, not 4: on a 1080-tall window the roomy leaf's four rows
     are 180px each against 182-184px of picture and three caption lines at the
     15-row floor, so every row overflowed by 2-4px, the fourth row's cost line
     was cut by the list's clip, and the lines had no leading at all. The
     spread, page, foot and slip below each give the leaf a few px for the same
     reason; together they are 47px, 11 per row. */
  gap: 2px 6px;
  flex: 1 1 auto;
  min-height: 0;
}
.hm-sb__blank { grid-column: 1 / -1; opacity: 0.65; text-align: center; padding-top: 24px; }
.hm-sb__cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  /* One pixel of gap and of padding: three caption lines at the 1080p floor
     (15 rows, 19 with a descender) under a 1:1 128px picture is 190px of a
     187px row, and the air is what gives. */
  gap: 2px;
  min-width: 0;
  min-height: 0;
  padding: 0 2px 0;
  border: 0;
  cursor: pointer;
  color: #2f2110;
  background: transparent;
  text-align: center;
}
.hm-sb__cell:hover .hm-sb__cellart { filter: brightness(1.08); }
.hm-sb__cellart {
  position: relative;
  flex: 0 0 auto;
  display: flex;
  padding: 1px;
  background-image:
    radial-gradient(52% 52% at 50% 46%, rgba(255,248,224,0.9) 0%, rgba(0,0,0,0) 74%),
    linear-gradient(168deg, #eaddb4 0%, #d3bd8e 58%, #b8a274 100%);
  box-shadow:
    inset 0 0 0 1px rgba(96,72,32,0.6),
    inset 0 0 0 2px rgba(248,238,208,0.45),
    0 1px 0 rgba(255,250,228,0.5),
    0 2px 5px rgba(60,40,12,0.35);
}
/* The chosen spell: a gilt rule round its picture, the way a reader marks a
   page, rather than a highlight bar across the leaf. */
.hm-sb__cell--on .hm-sb__cellart {
  box-shadow:
    inset 0 0 0 1px rgba(96,72,32,0.6),
    inset 0 0 0 2px rgba(248,238,208,0.45),
    0 0 0 2px #b8902c,
    0 0 0 3px rgba(60,40,12,0.55),
    0 0 12px rgba(217,178,90,0.55);
}
/* Measured 3.8:1 as #7c4c12 on the shaded parchment behind the chosen cell;
   #5a3208 is 6.4:1 there and still the warm brown of the reference. */
.hm-sb__cell--on .hm-sb__cellname { color: #5a3208; }
/* A spell the hero cannot cast right now keeps its picture at full colour -
   the reference never greys a page - and says so only in its caption's tone.
   The Cast plate and the reading page carry the refusal. */
.hm-sb__cell--barred .hm-sb__cellmeta,
/* Barred is quieter, not faint: #6b4f2a measured 3.7:1 on the shaded leaf
   (most of the book is barred outside a battle); #4a3414 is 5.6:1. */
.hm-sb__cell--barred .hm-sb__cellcost { color: #4a3414; }
.hm-sb__cellname {
  display: flex; justify-content: center;
  flex: 0 0 auto;
  max-width: 100%;
  margin-top: 0;
  line-height: 0;
  color: #3a2810;
  white-space: nowrap;
  /* The box of this span IS the caption's ink, so \`overflow: hidden\` clips
     the letterform itself: under a fractional device ratio the box lands on a
     fractional device pixel while crispEdges snaps each texel rectangle to a
     whole one, and the top row can be snapped just outside. Measured at 80%
     zoom, bottom row of the leaf: \`Raise Fallen\`, \`Riposte\` and
     \`Flame Spirit\` stood 10 rows of an 11-row face at 1366x768 and 14 of
     15 at 1920x1080 - flat-topped capitals. \`clip\` with a 2px margin still
     stops a name spilling into the next cell and costs no layout. */
  overflow: clip;
  overflow-clip-margin: 2px;
}
/* The level and the cost in the SAME ink as the name. The classic prints all
   three caption lines at one weight; a tan (#5f4a22) level line measured
   2:1 against the parchment and was a fuzz under the name at 1366. */
.hm-sb__cellmeta, .hm-sb__cellcost {
  display: flex; justify-content: center;
  margin-top: 0;
  line-height: 0;
  color: #3a2810;
  white-space: nowrap;
}
.hm-sb__cellname svg, .hm-sb__cellmeta svg, .hm-sb__cellcost svg { display: block; }
.hm-sb__kind { display: flex; gap: 10px; line-height: 0; color: #4a3414; }
.hm-sb__kind svg { display: block; }
.hm-sb__facts { display: flex; flex-direction: column; gap: 7px; }
.hm-sb__fact { display: flex; align-items: center; gap: 8px; line-height: 0; color: #3a2810; }
.hm-sb__fact svg { display: block; }
.hm-sb__factrule { flex: 1 1 auto; height: 1px; background-image: linear-gradient(90deg, rgba(74,52,20,0.35), rgba(74,52,20,0.35)); }
.hm-sb__cellcost--short { color: #8a2418; }

/* ---- the school ribbons.
   the classic hangs a ribbon for each school off the OUTER EDGE of the open
   book - violet for Air, green for Earth, red for Fire, indigo for Water - and
   two more off the other cover for what kind of book you are looking at. Ours
   were a row of pennant-shaped plates laid out along the foot of the verso
   page, and a blind comparison called them exactly what they were: "a 2024 web
   filter bar inside a grimoire". The shape was already right; the PLACE was the
   defect. A strip of controls inside the leaf is a toolbar however it is cut,
   and the same five ribbons hung off the cover are furniture on an object.

   They are absolutely positioned against .hs-book__spread, which is
   position: relative and carries 34px of leather either side (26 compact) -
   both of which had to be true before this could be done at all.
   This was said to be verified against
   a reference screenshot, "where two
   ribbons hang off the left cover and five off the right". THAT FILE DOES NOT
   EXIST, and there is no spellbook screen in any reference on disk - the
   critic's set is battle-heavy and its nine full screens are five battles, two
   adventure maps, a hero sheet and a town. So the two-and-five split is
   REMEMBERED, NOT CHECKED. The shape argument above stands on its own (a strip
   of controls inside a page is a toolbar however it is cut); the count and the
   sides are not evidence. */
.hm-sb__ribbons {
  position: absolute;
  top: 12%;
  z-index: 3;
  display: flex;
  flex-direction: column;
  gap: 9px;
}
.hm-sb__ribbons--left { left: 3px; align-items: flex-start; }
.hm-sb__ribbons--right { right: 3px; align-items: flex-end; }
.hm-sb__ribbon {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 46px;
  height: 40px;
  padding: 0;
  border: 0;
  cursor: pointer;
  background-color: #6a4d1c;
  background-image:
    radial-gradient(70% 50% at 32% 10%, rgba(255,244,214,0.34) 0%, rgba(255,244,214,0) 62%),
    linear-gradient(172deg, #a9853c 0%, #74551f 52%, #3f2d0e 100%);
  box-shadow:
    inset 0 0 0 1px rgba(0,0,0,0.85),
    inset 0 0 0 2px rgba(198,160,74,0.6),
    inset 0 2px 0 rgba(255,246,220,0.22),
    2px 3px 8px rgba(0,0,0,0.55);
  transition: filter 90ms linear, transform 90ms linear;
}
/* The free end is a swallow tail, and it points AWAY from the spine on each
   cover - which is the only thing that says the ribbon is hanging out of the
   book rather than lying on it. */
.hm-sb__ribbons--right .hm-sb__ribbon {
  clip-path: polygon(0% 0%, 100% 0%, 82% 50%, 100% 100%, 0% 100%);
}
.hm-sb__ribbons--left .hm-sb__ribbon {
  clip-path: polygon(100% 0%, 0% 0%, 18% 50%, 0% 100%, 100% 100%);
}
.hm-sb__sigil { display: block; width: 26px; height: 26px; }
.hm-sb__ribbons--right .hm-sb__sigil { margin-right: 6px; }
.hm-sb__ribbons--left .hm-sb__sigil { margin-left: 6px; }
.hm-sb__ribbon__mark {
  width: 9px;
  height: 9px;
  transform: rotate(45deg);
  background-image: linear-gradient(135deg, #ffeec2, #a8801f 55%, #3d2a08);
  box-shadow: 0 0 0 1px rgba(0,0,0,0.55);
}
.hm-sb__ribbons--right .hm-sb__ribbon__mark { margin-right: 7px; }
.hm-sb__ribbons--left .hm-sb__ribbon__mark { margin-left: 7px; }
.hm-sb__ribbon:hover { filter: brightness(1.16) saturate(1.06); }
/* Chosen: the ribbon is pulled OUT of the book a little and lit, the way a
   marker you have just used stands proud of the others. */
.hm-sb__ribbon--on {
  filter: brightness(1.3) saturate(1.08);
  box-shadow:
    inset 0 0 0 1px rgba(0,0,0,0.85),
    inset 0 0 0 2px rgba(255,226,150,0.95),
    inset 0 2px 0 rgba(255,250,230,0.35),
    3px 4px 10px rgba(0,0,0,0.6),
    0 0 14px rgba(217,178,90,0.5);
}
.hm-sb__ribbons--right .hm-sb__ribbon--on { transform: translateX(5px); }
.hm-sb__ribbons--left .hm-sb__ribbon--on { transform: translateX(-5px); }

.hm-sb__ribbon--air { background-image:
  radial-gradient(70% 50% at 32% 10%, rgba(226,214,255,0.32) 0%, rgba(226,214,255,0) 62%),
  linear-gradient(172deg, #6a55b4 0%, #45368a 52%, #241a4c 100%); }
.hm-sb__ribbon--earth { background-image:
  radial-gradient(70% 50% at 32% 10%, rgba(224,255,196,0.30) 0%, rgba(224,255,196,0) 62%),
  linear-gradient(172deg, #5f8f36 0%, #3f6323 52%, #1e3312 100%); }
.hm-sb__ribbon--fire { background-image:
  radial-gradient(70% 50% at 32% 10%, rgba(255,214,178,0.34) 0%, rgba(255,214,178,0) 62%),
  linear-gradient(172deg, #b4442a 0%, #82261a 52%, #45100a 100%); }
.hm-sb__ribbon--water { background-image:
  radial-gradient(70% 50% at 32% 10%, rgba(196,230,255,0.32) 0%, rgba(196,230,255,0) 62%),
  linear-gradient(172deg, #3a7fae 0%, #245a80 52%, #102c42 100%); }
.hm-sb__ribbon--air .hm-sb__ribbon__mark { background-image: linear-gradient(135deg, #dff0ff, #6f9fd0); }
.hm-sb__ribbon--earth .hm-sb__ribbon__mark { background-image: linear-gradient(135deg, #d9e6b0, #5f7a33); }
.hm-sb__ribbon--fire .hm-sb__ribbon__mark { background-image: linear-gradient(135deg, #ffd9a2, #b4441c); }
.hm-sb__ribbon--water .hm-sb__ribbon__mark { background-image: linear-gradient(135deg, #cfe9f5, #2f6b96); }
/* The two on the other cover are not schools, so they keep the book's own
   metal and are told apart by their device: steel for the battle half, green
   for the map half. */
.hm-sb__ribbon--combat .hm-sb__ribbon__mark {
  background-image: linear-gradient(135deg, #f2f4f8, #8c93a4 55%, #2c3340);
}
.hm-sb__ribbon--adv .hm-sb__ribbon__mark {
  background-image: linear-gradient(135deg, #dcecc4, #55803a 55%, #1e3312);
}

/* The folio at the foot of the index is a pennant hanging off the rule, which
   is where the classic puts its page furniture. */
.hm-sb__pennant {
  position: relative;
  margin-left: 0;
  padding: 4px 13px 8px;
  color: #f6e8c4;
  background-image:
    radial-gradient(70% 50% at 30% 8%, rgba(255,244,214,0.30) 0%, rgba(255,244,214,0) 62%),
    linear-gradient(172deg, #a9853c 0%, #74551f 52%, #3f2d0e 100%);
  box-shadow:
    inset 0 0 0 1px rgba(0,0,0,0.8),
    inset 0 0 0 2px rgba(198,160,74,0.55),
    0 3px 7px rgba(0,0,0,0.45);
  clip-path: polygon(0% 0%, 100% 0%, 100% 100%, 50% 74%, 0% 100%);
  text-shadow: 0 -1px 0 rgba(0,0,0,0.6);
}

/* ---- the cast plate.
   "The CAST plate reads as disabled even when it is not" was the third finding
   on this screen, and the cause is that the live plate and the dead one were
   the same object at two brightnesses: struck bronze at #b8913f against struck
   bronze at #6d6048. Two states of one control have to differ in KIND, not in
   level, or the player has to compare them to something to know which he has.

   So: live is polished gold, standing off the page with its own drop shadow and
   a warm halo, with dark type cut into it. Dead is the same plate SUNK into the
   leaf - an inset shadow, no lift, no halo, parchment-toned - which reads as
   "this is not available" rather than as "this is a slightly darker button". */
.hm-sb__cast {
  flex: 0 0 auto;
  padding: 10px 30px 11px;
  border: 0;
  cursor: pointer;
  font-family: var(--hm-font-display, 'Palatino Linotype', 'Book Antiqua', Palatino, Georgia, serif);
  font-size: max(15px, var(--hm-type-floor));
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: #2b1d04;
  background-color: #d5a93f;
  background-image:
    radial-gradient(82% 60% at 30% 6%, rgba(255,253,236,0.9) 0%, rgba(255,253,236,0) 60%),
    repeating-linear-gradient(41deg, rgba(255,244,212,0.06) 0 1px, rgba(0,0,0,0.05) 1px 4px),
    linear-gradient(172deg, #ffe9ab 0%, #d5a93f 46%, #8a6414 100%);
  box-shadow:
    inset 0 0 0 1px rgba(48,32,6,0.9),
    inset 0 2px 0 rgba(255,253,232,0.85),
    inset 0 -4px 8px rgba(96,60,10,0.45),
    0 2px 0 rgba(122,90,34,0.65),
    0 6px 14px rgba(0,0,0,0.4),
    0 0 20px rgba(232,196,110,0.5);
  text-shadow: 0 1px 0 rgba(255,250,220,0.75);
  clip-path: polygon(10px 0%, 100% 0%, 100% calc(100% - 10px), calc(100% - 10px) 100%, 0% 100%, 0% 10px);
  transition: filter 90ms linear, transform 90ms linear;
}
.hm-sb__cast:hover:not(:disabled) { filter: brightness(1.1) saturate(1.05); transform: translateY(-1px); }
.hm-sb__cast:active:not(:disabled) { transform: translateY(1px); }
/* Dead: the same plate SUNK into the leaf - a recessed cut in the parchment's
   own bronze, no hatch, no lift, no halo - with the stamped word dimmed. A flat
   grey hatched block is the web's disabled convention, not a carved one. */
.hm-sb__cast--off, .hm-sb__cast:disabled {
  cursor: not-allowed;
  transform: none;
  /* Dimmed by the PLATE, not by the word. A disabled control has to stay
     readable - a player has to know which control is unavailable - and the
     pale tan label on a tan plate measured 1.4:1, the one run on this screen
     under the 4.5:1 floor by any method. The classic greys the plate and keeps
     the label's dark keyline; this ink is 4.8:1 against the top of the sunk
     gradient, 5.7:1 against its middle and 6.5:1 against its foot. */
  color: #241a08;
  clip-path: none;
  background-color: #a8925f;
  background-image:
    linear-gradient(180deg, #9c8555 0%, #a8925f 40%, #b39d68 100%);
  box-shadow:
    inset 0 0 0 1px rgba(40,26,6,0.9),
    inset 0 4px 6px rgba(40,26,6,0.6),
    inset 3px 0 0 rgba(40,26,6,0.35),
    inset -3px 0 0 rgba(255,248,222,0.25),
    inset 0 -3px 0 rgba(255,248,222,0.35),
    inset 0 8px 14px rgba(40,26,6,0.30);
  text-shadow: none;
}
.hm-sb__cast svg { display: block; }
.hm-sb__cast { line-height: 0; }

/* The mastery ladder, when it carries anything: the same struck bronze as the
   mana die rather than four parchment cells with a hairline round them. It is
   only rendered when the four columns differ - four identical numbers in a
   750x50 band tell the player nothing. */
.hs-read__ladder .hs-rung {
  background:
    radial-gradient(74% 60% at 30% 12%, rgba(255,244,214,0.38) 0%, rgba(255,244,214,0) 62%),
    linear-gradient(172deg, #a9853c 0%, #74551f 52%, #3f2d0e 100%);
  box-shadow:
    inset 0 0 0 1px rgba(38,26,6,0.85),
    inset 0 1px 0 rgba(255,244,212,0.5),
    inset 0 -3px 5px rgba(0,0,0,0.4),
    0 1px 0 rgba(255,252,235,0.7),
    0 3px 6px rgba(88,64,26,0.5);
  clip-path: polygon(7px 0%, 100% 0%, 100% calc(100% - 7px), calc(100% - 7px) 100%, 0% 100%, 0% 7px);
}
.hs-read__ladder .hs-rung .hs-rung__cost { color: #fff3d2; text-shadow: 0 -1px 0 rgba(0,0,0,0.6); }
.hs-read__ladder .hs-rung .hs-rung__name { color: #e6cd97; }
.hs-read__ladder .hs-rung--on {
  background:
    radial-gradient(74% 60% at 30% 12%, rgba(255,255,240,0.72) 0%, rgba(255,255,240,0) 60%),
    linear-gradient(172deg, #ffeab4 0%, #d9b25a 48%, #97711a 100%);
  box-shadow:
    inset 0 0 0 1px rgba(48,34,8,0.9),
    inset 0 1px 0 rgba(255,255,240,0.8),
    0 0 12px rgba(217,178,90,0.45),
    0 2px 5px rgba(88,64,26,0.55);
}
.hs-read__ladder .hs-rung--on .hs-rung__cost { color: #2c2008; text-shadow: 0 1px 0 rgba(255,250,225,0.55); }
.hs-read__ladder .hs-rung--on .hs-rung__name { color: #4a3712; }

/* A resting page is not an error. Ink, serif, shadowed like every other line on
   the parchment - never the validator's red. */
.hm-sb__note {
  font-family: var(--hm-font-display, 'Palatino Linotype', 'Book Antiqua', Palatino, Georgia, serif);
  font-size: max(14px, var(--hm-type-floor));
  font-style: italic;
  /* #5b4520 measured 4.65:1 on the slip's parchment (209,186,140), under the
     4.5 floor once the italic thins it; the caption ink is 7.5:1 there. */
  color: #3a2810;
  text-shadow: 0 1px 0 rgba(255,248,222,0.6);
}
.hm-sb__flat {
  font-family: var(--hm-font-display, 'Palatino Linotype', 'Book Antiqua', Palatino, Georgia, serif);
  font-size: max(14px, var(--hm-type-floor));
  letter-spacing: 0.04em;
  color: #5b4520;
  text-shadow: 0 1px 0 rgba(255,248,222,0.6);
  padding: 8px 0 2px;
  background-image: linear-gradient(90deg,
    rgba(90,66,26,0.5) 0%, rgba(90,66,26,0.14) 72%, rgba(90,66,26,0) 100%);
  background-size: 100% 1px;
  background-position: 0 0;
  background-repeat: no-repeat;
}

/* ---- the desk the book is open on.
   Measured on the shipped 1080p capture with the 32px/sd<4 method: 24.7% of the
   screen read as flat, and 240 of those 490 blocks are the two outer margins
   either side of the spread - 140px of one gradient at 1920. The classic opens
   its book on a woven carpet, and a carpet is a repeat, so this is one. */
.hs-book { position: relative; }
.hs-book::before {
  content: "";
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-image:
    radial-gradient(circle at 50% 50%, rgba(206,162,92,0.16) 0 4px, rgba(0,0,0,0) 6px),
    radial-gradient(circle at 0% 0%, rgba(0,0,0,0.22) 0 6px, rgba(0,0,0,0) 8px),
    radial-gradient(circle at 100% 100%, rgba(0,0,0,0.22) 0 6px, rgba(0,0,0,0) 8px),
    repeating-linear-gradient(45deg, rgba(255,222,168,0.05) 0 2px, rgba(0,0,0,0.07) 2px 9px),
    repeating-linear-gradient(-45deg, rgba(255,222,168,0.04) 0 2px, rgba(0,0,0,0.06) 2px 9px);
  background-size: 46px 46px, 46px 46px, 46px 46px, auto, auto;
}

/* ---- the leaf's own ruled border and its dog-ear.
   A printed leaf of this period is ruled, and the corner a hand has taken a
   hundred times is turned. Both are page, not chrome: the rule is two hairlines
   inside the trim and the dog-ear is the paper itself, folded. */
.hs-book__page { position: relative; }
/* The roomy page and foot, taken in like the compact ones: see the leaf's row gap. */
.hm-sb__book .hs-book__page { padding: 8px 22px 6px; gap: 5px; }
.hm-sb__book .hs-book__foot { padding-top: 4px; }
.hs-book__page::before {
  content: "";
  position: absolute;
  inset: 7px;
  pointer-events: none;
  background-image:
    linear-gradient(rgba(122,90,36,0.5), rgba(122,90,36,0.5)),
    linear-gradient(rgba(122,90,36,0.5), rgba(122,90,36,0.5)),
    linear-gradient(rgba(122,90,36,0.5), rgba(122,90,36,0.5)),
    linear-gradient(rgba(122,90,36,0.5), rgba(122,90,36,0.5)),
    linear-gradient(rgba(122,90,36,0.28), rgba(122,90,36,0.28)),
    linear-gradient(rgba(122,90,36,0.28), rgba(122,90,36,0.28)),
    linear-gradient(rgba(122,90,36,0.28), rgba(122,90,36,0.28)),
    linear-gradient(rgba(122,90,36,0.28), rgba(122,90,36,0.28)),
    repeating-linear-gradient(91deg, rgba(112,84,42,0.085) 0 1px, rgba(0,0,0,0) 1px 4px),
    repeating-linear-gradient(1deg, rgba(112,84,42,0.05) 0 1px, rgba(0,0,0,0) 1px 6px),
    radial-gradient(circle at 26% 22%, rgba(146,108,50,0.14) 0 8px, rgba(0,0,0,0) 18px),
    radial-gradient(circle at 71% 69%, rgba(146,108,50,0.12) 0 11px, rgba(0,0,0,0) 23px);
  background-repeat:
    no-repeat, no-repeat, no-repeat, no-repeat,
    no-repeat, no-repeat, no-repeat, no-repeat,
    repeat, repeat, repeat, repeat;
  background-size:
    100% 1px, 100% 1px, 1px 100%, 1px 100%,
    calc(100% - 10px) 1px, calc(100% - 10px) 1px, 1px calc(100% - 10px), 1px calc(100% - 10px),
    auto, auto, 163px 141px, 211px 179px;
  background-position:
    left top, left bottom, left top, right top,
    5px 5px, 5px calc(100% - 5px), 5px 5px, calc(100% - 5px) 5px,
    0 0, 0 0, 0 0, 0 0;
}
.hs-book__page--left::after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 30px;
  height: 30px;
  pointer-events: none;
  clip-path: polygon(0 0, 100% 0, 0 100%);
  background-image: linear-gradient(135deg, #cbb083 0%, #b39a6d 48%, #94805a 100%);
  box-shadow: 1px 1px 0 rgba(88,64,26,0.4);
}

/* The description is set as a printed page is: a size larger, leaded, and with
   the initial dropped and painted. It is also what fills the recto - the blind
   read was "the right page is half-empty below the description", and a
   paragraph set at 14px in the middle of a 756px leaf is the reason. */
.hs-read__body.hm-sb__body {
  font-size: max(clamp(15px, 1.18vw, 22px), var(--hm-type-floor));
  line-height: 1.66;
}
.hm-sb__body::first-letter {
  float: left;
  padding: 3px 9px 0 0;
  font-size: 3.05em;
  line-height: 0.8;
  color: #7c4c12;
  text-shadow: 0 1px 0 rgba(255,250,226,0.85), 0 0 10px rgba(184,136,54,0.35);
}

/* ---- the slip under the book.
   The spread is two leaves of index and the reading lives on a slip of
   parchment tucked under the foot of the book, where the classic hangs its
   pennants. The book column stretches and the slip keeps its own height. */
.hs-book.hm-sb__book { flex-direction: column; align-items: stretch; gap: 8px; }
.hm-sb__book .hs-book__spread { flex: 1 1 auto; min-height: 0; padding: 8px 34px 12px; }
/* ---- the page block. A bound book has THICKNESS: under the two leaves and
   down their outer edges the stack of pages shows as a run of fine cream and
   tan lines, and that run is what tells a spread from two parchment
   rectangles on leather. The foot run is stepped - each page a shade shorter
   than the one above - and the side runs are the same lines turned. */
.hm-sb__book .hs-book__spread::after {
  content: "";
  position: absolute;
  left: 38px; right: 38px; bottom: 6px;
  height: 13px;
  pointer-events: none;
  background-image:
    linear-gradient(90deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0) 4%, rgba(0,0,0,0) 47%, rgba(0,0,0,0.55) 50%, rgba(0,0,0,0) 53%, rgba(0,0,0,0) 96%, rgba(0,0,0,0.35) 100%),
    repeating-linear-gradient(180deg, #efe0ba 0 1px, #b39a6c 1px 2px, #d8c497 2px 3px, #a08655 3px 4px);
  clip-path: polygon(0 0, 100% 0, 98.5% 100%, 1.5% 100%);
  box-shadow: 0 2px 3px rgba(0,0,0,0.6);
}
.hm-sb__book .hs-book__spread::before {
  content: "";
  position: absolute;
  top: 14px; bottom: 22px; left: 26px; right: 26px;
  pointer-events: none;
  background-image:
    repeating-linear-gradient(90deg, #efe0ba 0 1px, #b39a6c 1px 2px, #d8c497 2px 3px, #a08655 3px 4px),
    repeating-linear-gradient(90deg, #efe0ba 0 1px, #b39a6c 1px 2px, #d8c497 2px 3px, #a08655 3px 4px);
  background-size: 9px 100%, 9px 100%;
  background-position: left top, right top;
  background-repeat: no-repeat, no-repeat;
  clip-path: polygon(0 1%, 9px 0, 9px 100%, 0 99%, 0 1%, 100% 1%, calc(100% - 9px) 0, calc(100% - 9px) 100%, 100% 99%);
}
/* The spine: a rounded ridge lit from the head, the leaves pulled down
   into it. Narrower than the old flat strip and shaded across its width so
   it reads as a cylinder, with the signatures' stitch down the middle. */
.hm-sb__book .hs-book__gutter {
  width: 22px;
  margin: 0 -1px;
  background-image:
    repeating-linear-gradient(180deg, rgba(255,232,180,0.16) 0 2px, rgba(0,0,0,0.30) 2px 6px, rgba(0,0,0,0) 6px 41px),
    linear-gradient(90deg, #1b0f05 0%, #3c2410 22%, #6f4a22 50%, #3c2410 78%, #1b0f05 100%);
  background-size: 3px 100%, auto;
  background-position: 50% 0, 0 0;
  background-repeat: no-repeat, no-repeat;
  box-shadow: inset 0 0 0 1px rgba(0,0,0,0.7), 0 0 14px rgba(0,0,0,0.6);
}
/* The leaves CURL into the spine: a tight dark band at the inner margin over
   the wide soft one the kit paints, which is the difference between a page
   that is glued flat and one that is bound. */
.hm-sb__book .hs-book__page--left { box-shadow:
  inset 0 0 0 1px rgba(70,52,26,0.7),
  inset -6px 0 8px -4px rgba(50,32,10,0.7),
  inset -34px 0 40px rgba(70,48,18,0.42),
  inset 0 0 60px rgba(120,88,42,0.24); }
.hm-sb__book .hs-book__page--right { box-shadow:
  inset 0 0 0 1px rgba(70,52,26,0.7),
  inset 6px 0 8px -4px rgba(50,32,10,0.7),
  inset 34px 0 40px rgba(70,48,18,0.42),
  inset 0 0 60px rgba(120,88,42,0.24); }
.hm-sb__slip {
  position: relative;
  flex: 0 0 auto;
  display: flex;
  align-items: stretch;
  gap: 18px;
  width: 100%;
  max-width: 1640px;
  margin: 0 auto;
  padding: 6px 18px 6px 6px;
  background-color: #dcc697;
  background-image:
    var(--hm-vellum),
    radial-gradient(80% 60% at 92% 96%, rgba(140,102,48,0.28) 0%, rgba(0,0,0,0) 60%),
    radial-gradient(120% 140% at 50% 12%, #f2e3bd 0%, #dcc697 46%, #b79f72 100%);
  box-shadow:
    inset 0 0 0 1px rgba(70,52,26,0.75),
    inset 0 0 0 3px rgba(244,232,200,0.35),
    inset 0 0 40px rgba(120,88,42,0.24),
    0 6px 18px rgba(0,0,0,0.65);
}
.hm-sb__slip__empty { align-self: center; padding: 24px; opacity: 0.7; color: #4a3410; }
.hm-sb__slip .hs-read__plate { flex: 0 0 auto; align-self: center; }
.hm-sb__slip .hm-sb__illum { width: 136px; height: 136px; padding: 4px; }
.hm-sb__slip .hm-sb__illum::before { inset: 4px; }
.hm-sb__slip .hs-read { flex: 1 1 auto; min-width: 0; gap: 7px; justify-content: center; }
.hm-sb__slip .hs-read__banner { padding-bottom: 6px; gap: 12px; }
.hm-sb__slipname { display: flex; line-height: 0; color: #4a3410; }
.hm-sb__slipname svg { display: block; }
.hm-sb__slip .hs-read__body.hm-sb__body {
  font-size: max(15px, var(--hm-type-floor));
  line-height: 1.45;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.hm-sb__slip .hm-sb__body::first-letter {
  float: none; padding: 0; font-size: inherit; line-height: inherit; color: inherit; text-shadow: inherit;
}
.hm-sb__slip .hm-sb__facts { flex-direction: row; flex-wrap: wrap; gap: 6px 28px; }
.hm-sb__slip .hm-sb__factrule { flex: 0 0 14px; }
.hm-sb__slip .hs-read__act {
  flex: 0 0 auto; flex-direction: column; justify-content: center; align-items: flex-end;
  gap: 10px; padding: 0;
}
.hm-sb__slip .hm-sb__note, .hm-sb__slip .hs-read__ready { max-width: 250px; text-align: right; }
.hs-book--compact .hm-sb__book { gap: 6px; }
.hs-book--compact .hm-sb__book .hs-book__spread { padding: 9px 26px 18px; }
.hs-book--compact .hm-sb__book .hs-book__spread::after { left: 30px; right: 30px; height: 10px; bottom: 5px; }
.hs-book--compact .hm-sb__book .hs-book__spread::before { left: 19px; right: 19px; bottom: 18px; top: 12px; background-size: 7px 100%, 7px 100%;
  clip-path: polygon(0 1%, 7px 0, 7px 100%, 0 99%, 0 1%, 100% 1%, calc(100% - 7px) 0, calc(100% - 7px) 100%, 100% 99%); }
.hs-book--compact .hm-sb__slip { gap: 12px; padding: 5px 12px 5px 5px; }
.hs-book--compact .hm-sb__slip .hm-sb__illum { width: 140px; height: 140px; padding: 6px; }
.hs-book--compact .hm-sb__slip .hs-read { gap: 4px; }
.hs-book--compact .hm-sb__slip .hs-read__body.hm-sb__body { font-size: max(14px, var(--hm-type-floor)); line-height: 1.35; }
.hs-book--compact .hm-sb__slip .hm-sb__facts { gap: 4px 20px; }
.hs-book--compact .hm-sb__slip .hm-sb__body::first-letter {
  float: none; padding: 0; font-size: inherit; line-height: inherit; color: inherit; text-shadow: inherit;
}

/* The compact leaf's cell is 111.6px tall at 1366x768 for 68px of framed art
   and three 11-row lines: the name sat 1px under the frame, inside its 5px
   drop shadow and the chosen cell's 3px ring, with its ascenders shaved. Two
   px of cell padding become three px of leading under the frame, and the
   frame's shadow is drawn short so nothing of it reaches the caption. */
.hs-book--compact .hm-sb__cell { padding: 1px 2px 0; gap: 1px; }
.hs-book--compact .hm-sb__cellart + .hm-sb__cellname { margin-top: 3px; }
.hs-book--compact .hm-sb__cellart {
  box-shadow:
    inset 0 0 0 1px rgba(96,72,32,0.6),
    inset 0 0 0 2px rgba(248,238,208,0.45),
    0 1px 0 rgba(255,250,228,0.5),
    0 1px 2px rgba(60,40,12,0.35);
}
/* The compact leaf's margins, taken in so the fourth row's cost line clears
   the list's clip once the slip is under the spread: measured 3px short at
   1366x768 with polish.ts's 11/9px page padding. */
.hs-book--compact .hs-book__page { padding: 7px 12px 5px; gap: 4px; }
.hs-book--compact .hs-book__list.hm-sb__grid { gap: 2px 4px; }
.hs-book--compact .hs-book__foot { padding-top: 4px; }
.hs-book--compact .hm-sb__slip .hm-sb__illum { width: 136px; height: 136px; padding: 4px; }
.hs-book--compact .hm-sb__slip .hm-sb__illum::before { inset: 6px; }
.hs-book--compact .hm-sb__cellart { padding: 2px; }
.hs-book--compact .hm-sb__cellname { font-size: max(12px, var(--hm-type-floor)); }
.hs-book--compact .hm-sb__cellmeta, .hs-book--compact .hm-sb__cellcost { font-size: max(10px, var(--hm-type-floor)); }
.hs-book--compact .hm-sb__cast { padding: 7px 20px 8px; font-size: max(13px, var(--hm-type-floor)); }
.hs-book--compact .hm-sb__ribbon { width: 34px; height: 30px; }
.hs-book--compact .hm-sb__sigil { width: 20px; height: 20px; }
.hs-book--compact .hm-sb__ribbons--right .hm-sb__sigil { margin-right: 4px; }
.hs-book--compact .hm-sb__ribbons--left .hm-sb__sigil { margin-left: 4px; }
.hs-book--compact .hm-sb__ribbons { gap: 6px; }
.hs-book--compact .hm-sb__ribbons--left { left: 2px; }
.hs-book--compact .hm-sb__ribbons--right { right: 2px; }
.hs-book--compact .hm-sb__illum { padding: 10px; }
.hs-book--compact .hm-sb__illum::before { inset: 8px; }
.hs-book--compact .hm-sb__body::first-letter { font-size: 2.6em; padding-right: 7px; }
.hm-sb__slip .hm-sb__body::first-letter, .hs-book--compact .hm-sb__slip .hm-sb__body::first-letter {
  float: none; padding: 0; font-size: inherit; line-height: inherit; color: inherit; text-shadow: inherit;
}
`;

function useBookStyles(): void {
  useStyleSheet(BOOK_STYLE_ID, BOOK_CSS, StyleLayer.screen);
}
